import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiCalendar, FiDollarSign, FiTrendingUp, FiUsers, FiFileText,
  FiArrowRight, FiTarget, FiMapPin, FiGlobe, FiBriefcase,
  FiPercent, FiBarChart2, FiUser, FiExternalLink, FiTag,
  FiLayers, FiActivity, FiClock
} from 'react-icons/fi';
import { api } from '../api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useResearchAgent } from '../context/ResearchAgentContext';
import { useDevice } from '../context/DeviceContext';
import './DealDetail.css';

function DealDetail() {
  const { pitchbookDealId } = useParams();
  const navigate = useNavigate();
  const { updatePageContext, clearPageContext } = useResearchAgent();
  const { isMobile } = useDevice();
  const [deal, setDeal] = useState(null);
  const [matchedInvestors, setMatchedInvestors] = useState([]);
  const [linkedCompany, setLinkedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mobile collapsible sections - DOM-based approach
  useEffect(() => {
    if (!isMobile) return;

    const sectionTitles = document.querySelectorAll('.deal-detail-page .section-title, .deal-detail-page .subsection-title');

    const handleClick = (e) => {
      const title = e.currentTarget;
      const isCollapsed = title.classList.toggle('collapsed');

      let sibling = title.nextElementSibling;
      while (sibling && !sibling.classList.contains('section-title') && !sibling.classList.contains('subsection-title')) {
        if (isCollapsed) {
          sibling.classList.add('section-collapsed');
        } else {
          sibling.classList.remove('section-collapsed');
        }
        sibling = sibling.nextElementSibling;
      }
    };

    sectionTitles.forEach(title => {
      title.addEventListener('click', handleClick);
      title.style.cursor = 'pointer';
    });

    return () => {
      sectionTitles.forEach(title => {
        title.removeEventListener('click', handleClick);
      });
    };
  }, [isMobile, deal]);

  useEffect(() => {
    fetchDealData();
  }, [pitchbookDealId]);

  // Update Research Agent page context when deal data loads
  useEffect(() => {
    if (deal) {
      // Parse investors from the deal's investors field (handles both array and string)
      const investorsList = Array.isArray(deal.investors)
        ? deal.investors.filter(Boolean)
        : typeof deal.investors === 'string'
          ? deal.investors.split(',').map(i => i.trim()).filter(Boolean)
          : [];
      updatePageContext('deal-detail', {
        deal_type: deal.deal_type,
        company_name: deal.companies,
        deal_date: deal.deal_date,
        deal_size: deal.deal_size_usd,
        investors: investorsList.slice(0, 5),
        pitchbook_id: deal.pitchbook_deal_id
      });
    }
    // Clear context when leaving the page
    return () => clearPageContext();
  }, [deal, updatePageContext, clearPageContext]);

  const fetchDealData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch deal data, matched investors, and linked company in parallel
      const [dealsData, investorsData, companyData] = await Promise.all([
        api.getDealsFull({ search: pitchbookDealId, limit: 1 }),
        api.getDealInvestors(pitchbookDealId).catch(() => []),
        api.getDealCompany(pitchbookDealId).catch(() => ({ company: null, matched: false }))
      ]);

      if (!dealsData || dealsData.length === 0) {
        throw new Error('Deal not found');
      }
      setDeal(dealsData[0]);

      // Build a lookup map for matched investors by normalized name
      setMatchedInvestors(investorsData || []);

      // Set linked company if matched
      if (companyData?.matched && companyData?.company) {
        setLinkedCompany(companyData.company);
      }
    } catch (err) {
      setError('Deal not found or failed to load.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if an investor name matches a known investor
  const getMatchedInvestor = (investorName) => {
    if (!matchedInvestors.length) return null;
    // Match by investor_name_in_deal (the raw name from the deal)
    return matchedInvestors.find(mi =>
      mi.investor_name_in_deal === investorName ||
      mi.investor_name?.toLowerCase() === investorName?.toLowerCase()
    );
  };

  // Helper to parse array strings from database
  const parseArrayField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    // Handle PostgreSQL array format: {item1,item2}
    if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
      return value.slice(1, -1).split(',').map(s => s.trim()).filter(Boolean);
    }
    // Handle comma-separated string
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Helper to get acquirer from investors in M&A deals
  const getAcquirer = () => {
    if (!deal) return null;
    // For M&A deals, the investor is typically the acquirer
    if (deal.deal_type === 'Merger/Acquisition' || deal.deal_type_2?.includes('Acquisition')) {
      const investors = parseArrayField(deal.investors || deal.leadsole_investors);
      return investors.length > 0 ? investors[0] : null;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading deal details...</p>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="error-container">
        <h2>{error || 'Deal not found'}</h2>
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
      </div>
    );
  }

  const acquirer = getAcquirer();
  const companyName = typeof deal.companies === 'string'
    ? (deal.companies.split(',')[0]?.trim() || 'Unknown Company')
    : 'Unknown Company';
  const verticals = parseArrayField(deal.verticals);
  const investors = parseArrayField(deal.investors);
  const leadInvestors = parseArrayField(deal.leadsole_investors);

  return (
    <div className="deal-detail-page">
      {/* Header with Company Name Hero */}
      <div className="detail-header">
        <div className="header-left">
          {/* Company Name - Hero */}
          <div className="company-hero">
            <h1 className="company-hero-name">
              {linkedCompany ? (
                <Link
                  to={`/company/${linkedCompany.pitchbook_company_id}`}
                  className="company-name-link"
                >
                  {companyName}
                </Link>
              ) : (
                companyName
              )}
            </h1>

            {/* For M&A deals, show acquirer */}
            {acquirer && (
              <div className="deal-parties">
                <span className="deal-party-label">Acquired by</span>
                <span className="deal-party-arrow">→</span>
                <span className="acquirer-name">{acquirer}</span>
              </div>
            )}
          </div>

          {/* Deal Type as subtitle */}
          <div className="deal-title">
            {deal.deal_type || 'Transaction'}
            {deal.deal_type_2 && <span className="deal-type-secondary"> • {deal.deal_type_2}</span>}
            {deal.deal_type_3 && <span className="deal-type-secondary"> • {deal.deal_type_3}</span>}
          </div>
        </div>
      </div>

      {/* Tags and Meta Row */}
      <div className="header-links-row">
        <div className="header-content">
          {/* Tags row */}
          <div className="header-tags">
            {deal.deal_date && (
              <span className="date-tag">
                <FiCalendar size={14} />
                {formatDate(deal.deal_date)}
              </span>
            )}
            {deal.deal_status && (
              <span className="status-tag">{deal.deal_status}</span>
            )}
            {deal.financing_status && (
              <span className="status-tag">{deal.financing_status}</span>
            )}
          </div>

          {/* Meta tags for industry/location */}
          <div className="header-meta">
            {deal.primary_industry_code && (
              <span className="meta-tag">
                <FiBriefcase size={12} />
                {deal.primary_industry_code}
              </span>
            )}
            {deal.hq_location && (
              <span className="meta-tag">
                <FiMapPin size={12} />
                {deal.hq_location}
              </span>
            )}
            {deal.year_founded && (
              <span className="meta-tag">
                <FiClock size={12} />
                Est. {deal.year_founded}
              </span>
            )}
            {deal.company_website && (
              <a href={deal.company_website.startsWith('http') ? deal.company_website : `https://${deal.company_website}`}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="meta-tag"
                 style={{ textDecoration: 'none' }}>
                <FiGlobe size={12} />
                Website
                <FiExternalLink size={10} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Key Highlights Section - Deal Metrics */}
      <div className="highlights-section">
        <h2 className="section-title">Deal Highlights</h2>
        <div className="highlights-row">
          {/* Deal Size */}
          {deal.deal_size_usd && !isNaN(parseFloat(deal.deal_size_usd)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiDollarSign size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Deal Size</span>
                <span className="highlight-value">{formatCurrency(deal.deal_size_usd)}</span>
                {deal.deal_size_status && (
                  <span className="highlight-subtext">{deal.deal_size_status}</span>
                )}
              </div>
            </div>
          )}

          {/* Post-Money Valuation */}
          {deal.post_valuation_usd && !isNaN(parseFloat(deal.post_valuation_usd)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTarget size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Post-Money Valuation</span>
                <span className="highlight-value">{formatCurrency(deal.post_valuation_usd)}</span>
                {deal.post_valuation_status && (
                  <span className="highlight-subtext">{deal.post_valuation_status}</span>
                )}
              </div>
            </div>
          )}

          {/* Pre-Money Valuation */}
          {deal.pre_money_valuation && !isNaN(parseFloat(deal.pre_money_valuation)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiTrendingUp size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Pre-Money Valuation</span>
                <span className="highlight-value">{formatCurrency(deal.pre_money_valuation)}</span>
              </div>
            </div>
          )}

          {/* Revenue */}
          {deal.revenue && !isNaN(parseFloat(deal.revenue)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiBarChart2 size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Revenue</span>
                <span className="highlight-value">{formatCurrency(deal.revenue)}</span>
                {deal.fiscal_year && (
                  <span className="highlight-subtext">FY {deal.fiscal_year}</span>
                )}
              </div>
            </div>
          )}

          {/* % Acquired (for M&A) */}
          {deal.percent_acquired && !isNaN(parseFloat(deal.percent_acquired)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiPercent size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Stake Acquired</span>
                <span className="highlight-value">{parseFloat(deal.percent_acquired).toFixed(1)}%</span>
              </div>
            </div>
          )}

          {/* Valuation Step-Up */}
          {deal.valuation_step_up && !isNaN(parseFloat(deal.valuation_step_up)) && (
            <div className="highlight-card">
              <div className="highlight-icon">
                <FiArrowRight size={24} />
              </div>
              <div className="highlight-content">
                <span className="highlight-label">Valuation Step-Up</span>
                <span className="highlight-value">{parseFloat(deal.valuation_step_up).toFixed(2)}x</span>
                <span className="highlight-subtext">From previous round</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Profile - Full Width at Top */}
      <div className="info-card" style={{ marginBottom: 30 }}>
        <h2 className="section-title">
          <FiBriefcase size={20} />
          Company Profile
          {linkedCompany && (
            <Link
              to={`/company/${linkedCompany.pitchbook_company_id}`}
              className="company-profile-link"
            >
              View Full Profile <FiExternalLink size={14} />
            </Link>
          )}
        </h2>

        {/* Linked Company Card - when we have a match */}
        {linkedCompany && (
          <Link
            to={`/company/${linkedCompany.pitchbook_company_id}`}
            className="linked-company-card"
            style={{ textDecoration: 'none' }}
          >
            <div className="linked-company-header">
              <div className="linked-company-name">{linkedCompany.company_name}</div>
              <FiExternalLink size={16} className="linked-company-icon" />
            </div>
            <div className="linked-company-meta">
              {linkedCompany.primary_industry_code && (
                <span className="linked-company-tag">
                  <FiBriefcase size={12} />
                  {linkedCompany.primary_industry_code}
                </span>
              )}
              {linkedCompany.hq_location && (
                <span className="linked-company-tag">
                  <FiMapPin size={12} />
                  {linkedCompany.hq_location}
                </span>
              )}
              {linkedCompany.year_founded && (
                <span className="linked-company-tag">
                  <FiClock size={12} />
                  Est. {linkedCompany.year_founded}
                </span>
              )}
              {linkedCompany.employees && (
                <span className="linked-company-tag">
                  <FiUsers size={12} />
                  {parseInt(linkedCompany.employees).toLocaleString()} employees
                </span>
              )}
            </div>
            {linkedCompany.total_raised && (
              <div className="linked-company-raised">
                Total Raised: {formatCurrency(linkedCompany.total_raised)}
              </div>
            )}
            {linkedCompany.business_status && (
              <span className={`linked-company-status status-${linkedCompany.business_status?.toLowerCase().replace(/\s+/g, '-')}`}>
                {linkedCompany.business_status}
              </span>
            )}
          </Link>
        )}

        {/* Description */}
        {deal.description && (
          <div className="info-content" style={{ marginTop: linkedCompany ? 16 : 0 }}>
            <p className="synopsis-text">{deal.description}</p>
          </div>
        )}
      </div>

      {/* Main Content - Three Columns */}
      <div className="deal-info-three-col">
        {/* Column 1: Deal Synopsis & Company Metrics */}
        <div className="info-card">
          {/* Deal Synopsis */}
          {deal.deal_synopsis && (
            <>
              <h2 className="section-title">
                <FiFileText size={20} />
                Deal Synopsis
              </h2>
              <div className="info-content" style={{ marginBottom: 20 }}>
                <p className="synopsis-text" style={{ fontSize: 14 }}>
                  {deal.deal_synopsis}
                </p>
              </div>
            </>
          )}

          {/* Key Company Metrics */}
          <div className="key-metrics-grid">
            {deal.employees && (
              <div className="metric-item">
                <span className="metric-label">Employees</span>
                <span className="metric-value">{parseInt(deal.employees).toLocaleString()}</span>
              </div>
            )}
            {deal.year_founded && (
              <div className="metric-item">
                <span className="metric-label">Founded</span>
                <span className="metric-value">{deal.year_founded}</span>
              </div>
            )}
            {deal.current_employees && (
              <div className="metric-item">
                <span className="metric-label">Current Employees</span>
                <span className="metric-value">{parseInt(deal.current_employees).toLocaleString()}</span>
              </div>
            )}
            {deal.raised_to_date && (
              <div className="metric-item">
                <span className="metric-label">Raised to Date</span>
                <span className="metric-value">{formatCurrency(deal.raised_to_date)}</span>
              </div>
            )}
          </div>

          {/* Verticals */}
          {verticals.length > 0 && (
            <>
              <h3 className="info-label" style={{ marginTop: 24, marginBottom: 0 }}>
                <FiTag size={14} />
                Verticals
              </h3>
              <div className="verticals-grid">
                {verticals.slice(0, 8).map((vertical, idx) => (
                  <span key={idx} className="vertical-tag">{vertical}</span>
                ))}
              </div>
            </>
          )}

          {/* CEO Info */}
          {deal.ceo_at_time_of_deal && (
            <div className="ceo-card">
              <div className="ceo-header">
                <div className="ceo-avatar">
                  {deal.ceo_at_time_of_deal.charAt(0)}
                </div>
                <div>
                  <div className="ceo-name">{deal.ceo_at_time_of_deal}</div>
                  <div className="ceo-title">CEO at time of deal</div>
                </div>
              </div>
              {deal.ceo_biography && (
                <p className="ceo-bio">
                  {deal.ceo_biography.length > 300
                    ? `${deal.ceo_biography.substring(0, 300)}...`
                    : deal.ceo_biography}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Column 2: Investors */}
        <div className="info-card">
          <h2 className="section-title">
            <FiUsers size={20} />
            Investors
            {investors.length > 0 && (
              <span className="investor-count">{investors.length}</span>
            )}
          </h2>

          {/* Lead/Sole Investors */}
          {leadInvestors.length > 0 && (
            <>
              <h3 className="info-label" style={{ marginBottom: 12 }}>Lead Investors</h3>
              <div className="investors-list" style={{ marginTop: 0, marginBottom: 24 }}>
                {leadInvestors.map((investor, idx) => {
                  const matched = getMatchedInvestor(investor);
                  return matched ? (
                    <Link
                      key={idx}
                      to={`/investor/${matched.pitchbook_investor_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="investor-item investor-item-linked investor-item-matched"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="investor-icon investor-icon-matched">
                        <FiBriefcase size={16} />
                      </div>
                      <div className="investor-info">
                        <span className="investor-name investor-name-linked">{investor}</span>
                        <div className="investor-meta">
                          {matched.investor_type && (
                            <span className="investor-type-badge">{matched.investor_type}</span>
                          )}
                          {matched.hq_location && (
                            <span className="investor-location"><FiMapPin size={10} /> {matched.hq_location}</span>
                          )}
                        </div>
                      </div>
                      <FiExternalLink size={14} className="investor-link-icon" />
                    </Link>
                  ) : (
                    <div key={idx} className="investor-item">
                      <div className="investor-icon">
                        <FiUsers size={16} />
                      </div>
                      <div className="investor-info">
                        <span className="investor-name">{investor}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* All Investors */}
          {investors.length > 0 && investors.length !== leadInvestors.length && (
            <>
              <h3 className="info-label" style={{ marginBottom: 12 }}>All Investors</h3>
              <div className="investors-list" style={{ marginTop: 0 }}>
                {investors.slice(0, 15).map((investor, idx) => {
                  const matched = getMatchedInvestor(investor);
                  return matched ? (
                    <Link
                      key={idx}
                      to={`/investor/${matched.pitchbook_investor_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="investor-item investor-item-linked investor-item-matched"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="investor-icon investor-icon-matched">
                        <FiBriefcase size={16} />
                      </div>
                      <div className="investor-info">
                        <span className="investor-name investor-name-linked">{investor}</span>
                        <div className="investor-meta">
                          {matched.investor_type && (
                            <span className="investor-type-badge">{matched.investor_type}</span>
                          )}
                          {matched.hq_location && (
                            <span className="investor-location"><FiMapPin size={10} /> {matched.hq_location}</span>
                          )}
                        </div>
                      </div>
                      <FiExternalLink size={14} className="investor-link-icon" />
                    </Link>
                  ) : (
                    <div key={idx} className="investor-item">
                      <div className="investor-icon">
                        <FiUser size={16} />
                      </div>
                      <div className="investor-info">
                        <span className="investor-name">{investor}</span>
                      </div>
                    </div>
                  );
                })}
                {investors.length > 15 && (
                  <p style={{ color: '#6c757d', fontSize: 13, marginTop: 8 }}>
                    + {investors.length - 15} more investors
                  </p>
                )}
              </div>
            </>
          )}

          {investors.length === 0 && leadInvestors.length === 0 && (
            <div className="no-data">
              <p>No investor information available</p>
            </div>
          )}

          {/* Investor Stats */}
          {(deal.num_new_investors || deal.num_follow_on_investors) && (
            <div className="key-metrics-grid" style={{ marginTop: 20 }}>
              {deal.num_new_investors && (
                <div className="metric-item">
                  <span className="metric-label">New Investors</span>
                  <span className="metric-value">{deal.num_new_investors}</span>
                </div>
              )}
              {deal.num_follow_on_investors && (
                <div className="metric-item">
                  <span className="metric-label">Follow-on</span>
                  <span className="metric-value">{deal.num_follow_on_investors}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Column 3: Transaction Details & Financials */}
        <div className="info-card">
          <h2 className="section-title">
            <FiActivity size={20} />
            Transaction Details
          </h2>

          <div className="info-grid">
            {deal.deal_date && (
              <div className="info-item">
                <span className="info-label">
                  <FiCalendar size={14} />
                  Deal Date
                </span>
                <span className="info-value">{formatDate(deal.deal_date)}</span>
              </div>
            )}
            {deal.announced_date && deal.announced_date !== deal.deal_date && (
              <div className="info-item">
                <span className="info-label">Announced</span>
                <span className="info-value">{formatDate(deal.announced_date)}</span>
              </div>
            )}
            {deal.deal_type && (
              <div className="info-item">
                <span className="info-label">Deal Type</span>
                <span className="info-value">{deal.deal_type}</span>
                {deal.deal_type_2 && (
                  <span className="info-subtext">{deal.deal_type_2}</span>
                )}
              </div>
            )}
            {deal.deal_class && (
              <div className="info-item">
                <span className="info-label">Deal Class</span>
                <span className="info-value">{deal.deal_class}</span>
              </div>
            )}
            {deal.deal_status && (
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className="info-value">{deal.deal_status}</span>
              </div>
            )}
            {deal.business_status && (
              <div className="info-item">
                <span className="info-label">Business Status</span>
                <span className="info-value">{deal.business_status}</span>
              </div>
            )}
            {deal.vc_round && (
              <div className="info-item">
                <span className="info-label">VC Round</span>
                <span className="info-value">{deal.vc_round}</span>
                {deal.vc_round_up_down_flat && (
                  <span className="info-subtext">{deal.vc_round_up_down_flat}</span>
                )}
              </div>
            )}
            {deal.series && (
              <div className="info-item">
                <span className="info-label">Series</span>
                <span className="info-value">{deal.series}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Financial Metrics - Standalone Section */}
      {(deal.ebitda || deal.net_income || deal.valuation_revenue || deal.implied_ev || deal.valuation_ebitda || deal.ebitda_margin_percent) && (
        <div className="info-card" style={{ marginTop: 30 }}>
          <h2 className="section-title">
            <FiBarChart2 size={20} />
            Financial Metrics
          </h2>
          <div className="key-metrics-grid">
            {deal.implied_ev && (
              <div className="metric-item">
                <span className="metric-label">Implied EV</span>
                <span className="metric-value">{formatCurrency(deal.implied_ev)}</span>
              </div>
            )}
            {deal.ebitda && (
              <div className="metric-item">
                <span className="metric-label">EBITDA</span>
                <span className="metric-value">{formatCurrency(deal.ebitda)}</span>
              </div>
            )}
            {deal.net_income && (
              <div className="metric-item">
                <span className="metric-label">Net Income</span>
                <span className="metric-value">{formatCurrency(deal.net_income)}</span>
              </div>
            )}
            {deal.valuation_revenue && (
              <div className="metric-item">
                <span className="metric-label">EV/Revenue</span>
                <span className="metric-value">{parseFloat(deal.valuation_revenue).toFixed(2)}x</span>
              </div>
            )}
            {deal.valuation_ebitda && (
              <div className="metric-item">
                <span className="metric-label">EV/EBITDA</span>
                <span className="metric-value">{parseFloat(deal.valuation_ebitda).toFixed(2)}x</span>
              </div>
            )}
            {deal.ebitda_margin_percent && (
              <div className="metric-item">
                <span className="metric-label">EBITDA Margin</span>
                <span className="metric-value">{parseFloat(deal.ebitda_margin_percent).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Additional Service Providers Section - if data exists */}
      {(deal.service_providers_all || deal.sellers || deal.lenders) && (
        <div className="info-card" style={{ marginTop: 30 }}>
          <h2 className="section-title">
            <FiLayers size={20} />
            Deal Participants
          </h2>
          <div className="detail-content" style={{ gap: 20 }}>
            {deal.sellers && (
              <div className="info-item">
                <span className="info-label">Sellers</span>
                <span className="info-value">{deal.sellers}</span>
              </div>
            )}
            {deal.lenders && (
              <div className="info-item">
                <span className="info-label">Lenders</span>
                <span className="info-value">{deal.lenders}</span>
              </div>
            )}
            {deal.service_providers_sell_side && (
              <div className="info-item">
                <span className="info-label">Sell-Side Advisors</span>
                <span className="info-value" style={{ fontSize: 14 }}>{deal.service_providers_sell_side}</span>
              </div>
            )}
            {deal.service_providers_buy_side && (
              <div className="info-item">
                <span className="info-label">Buy-Side Advisors</span>
                <span className="info-value" style={{ fontSize: 14 }}>{deal.service_providers_buy_side}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DealDetail;
